<template>
    <validation-observer ref="observer" v-slot="{ invalid }">
      <b-modal
        :id="`${addCandidateModalType || 'selection-screen'}`"
        :modalClass= "`${addCandidateModalType || 'selection-screen'}`"
        v-model="isVisible"
        @hidden="onHide"
        no-close-on-backdrop
        centered
        hideFooter
      >
          <div slot="modal-header" class="d-flex justify-content-between w-100">
              <h2>{{ modalTitle }}</h2>
              <i
              class="icon-close custom-modal-close cursor-pointer"
              @click="onHide"
              />
          </div>
          <div class="modal-progress" v-if="totalSteps > 1 && !showSelectionScreen" :style="{ '--progress-width': progressPercentage }"></div>

          <!-- Selection Screen -->
          <modal-selection-screen
            v-if="showSelectionScreen && !selectedModalType"
            @showAddCandidateModalHandler="showAddCandidateModalHandler"
            @onHide="onHide"
          />

          <!-- Modal Content -->
          <component
            v-else
            :is="currentModalType"
            :candidate="candidate"
            :dateRange.sync="dateRange"
            :invalid="invalid"
            :buttonLoading="buttonLoading"
            :currentStep="currentStep"
            :totalSteps="totalSteps"
            :showSuccessStep="showSuccessStep"
            :sendableCount="sendableCount"
            :sendedCandidates="sendedCandidates"
            :unSendedCandidates="unSendedCandidates"
            :totalSendedCount="totalSendedCount"
            :interviewType="addCandidateModalInterviewType"
            :isDateSet="addCandidateModalData?.setDate || false"
            :scheduleResendModal="addCandidateModalData?.scheduleResendModal || false"
            :bulkSchedule="addCandidateModalData?.bulkSchedule || false"
            @downloadExcel="downloadExcel"
            @updateCandidate="candidate = $event"
            @onValidateField="onValidateField"
            @next="nextStep"
            @prev="prevStep"
            @submit="saveCandidate"
            @onHide="onHide"
          />
      </b-modal>
    </validation-observer>
  </template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { utils, writeFile } from 'xlsx';
import excelModal from './addCandidateModals/excelModal.vue';
import oneByOneModal from './addCandidateModals/oneByOneModal.vue';
import atsModal from './addCandidateModals/atsModal.vue';
import modalSelectionScreen from './addCandidateModals/modalSelectionScreen.vue';
import { showNotification } from '../../static/helpers';
import hrvenueService from '../../services/hrvenue.service';

export default {
  name: 'AddCandidateModal',
  components: {
    oneByOneModal,
    excelModal,
    atsModal,
    modalSelectionScreen,
  },
  props: {
    interviewId: {
      type: String,
      required: true,
    },
    showSelectionScreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentStep: 1,
      sendableCount: 0,
      creditCount: 0,
      buttonLoading: false,
      showSuccessStep: false,
      candidate: {
        candidates: [{
          name: '',
          surname: '',
          email: '',
        }],
      },
      dateRange: {
        startDate: new Date(),
        endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
        startTime: null,
        endTime: null,
        selectedDuration: '7',
      },
      sendedCandidates: [],
      unSendedCandidates: [],
      totalSendedCount: 0,
      selectedModalType: null,
    };
  },
  watch: {
    isAddCandidateModalVisible(value) {
      if (value && this.addCandidateModalData && this.currentModalType === 'one-by-one-modal') {
        this.candidate.candidates = [{
          name: this.addCandidateModalData.candidate.name,
          surname: this.addCandidateModalData.candidate.surname,
          email: this.addCandidateModalData.candidate.email,
        }];

        if (this.addCandidateModalData?.setDate) {
          const startDate = new Date(`${this.addCandidateModalData.candidate.sentDate}Z`);
          const endDate = new Date(`${this.addCandidateModalData.candidate.expireDate}Z`);
          if (startDate > Date.now()) {
            const diffMs = endDate - startDate;
            const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
            const startTime = startDate.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
            const endTime = endDate.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
            this.dateRange = {
              startDate,
              endDate,
              selectedDuration: diffDays.toString(),
              startTime,
              endTime,
            };
            this.tempDateRange = this.dateRange;
          }
        }
      } else {
        this.dateRange = {
          startDate: new Date(),
          endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
          startTime: null,
          endTime: null,
          selectedDuration: '7',
        };
        this.tempDateRange = this.dateRange;
      }
    },
  },
  async mounted() {
    const { sendableCount } = await hrvenueService.getSendableCount();
    this.sendableCount = sendableCount;
  },
  computed: {
    ...mapGetters('addCandidateModals', [
      'isAddCandidateModalVisible',
      'addCandidateModalType',
      'addCandidateModalData',
      'addCandidateModalInterviewType',
    ]),
    isVisible: {
      get() {
        return this.isAddCandidateModalVisible;
      },
      set(value) {
        if (!value) {
          this.hideAddCandidateModal();
        }
      },
    },
    currentModalType() {
      return this.selectedModalType || this.addCandidateModalType;
    },
    modalTitle() {
      if (this.showSelectionScreen && !this.selectedModalType) {
        return this.resource.addCandidateModalSelectionTitle || 'Select Interview Type';
      }

      const titles = {
        'one-by-one-modal': this.addCandidateModalData?.setDate ? this.resource.scheduleEditModalTitle : this.resource.addCandidateModalOneByOneTitle,
        'excel-modal': this.resource.addCandidateModalExcelTitle,
        'ats-modal': this.resource.addCandidateModalAtsTitle,
      };

      titles['one-by-one-modal'] = this.addCandidateModalData?.scheduleResendModal
        ? this.resource.scheduleResendModalTitle
        : titles['one-by-one-modal'];

      return titles[this.currentModalType];
    },
    totalSteps() {
      const steps = {
        'one-by-one-modal': 1,
        'excel-modal': 2,
        'ats-modal': 2,
      };
      return steps[this.currentModalType] || 1;
    },
    progressPercentage() {
      return `${(this.currentStep / this.totalSteps) * 100}%`;
    },
  },
  methods: {
    ...mapActions('addCandidateModals', [
      'hideAddCandidateModal',
    ]),
    showAddCandidateModalHandler(modalType) {
      this.selectedModalType = modalType;
      this.resetForm();
      this.$emit('showAddCandidateModalHandler', modalType);
    },
    onHide() {
      this.hideAddCandidateModal();
      this.resetForm();
      this.selectedModalType = null;
    },
    resetForm() {
      this.currentStep = 1;
      this.candidate = {
        candidates: [{
          name: '',
          surname: '',
          email: '',
        }],
      };
      setTimeout(() => {
        this.showSuccessStep = false;
      }, 300);
      this.buttonLoading = false;
      this.$refs.observer.reset();
    },
    nextStep() {
      if (this.currentStep < this.totalSteps) {
        // eslint-disable-next-line no-plusplus
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        // eslint-disable-next-line no-plusplus
        this.currentStep--;
      }
    },
    isSameDate(d1, d2) {
      return (
        d1
        && d2
        && new Date(d1).getFullYear() === new Date(d2).getFullYear()
        && new Date(d1).getMonth() === new Date(d2).getMonth()
        && new Date(d1).getDate() === new Date(d2).getDate()
      );
    },
    isSameTime(t1, t2) {
      // t1 ve t2 string ise: "HH:mm" formatında karşılaştır
      return t1 === t2;
    },
    async saveCandidate(values) {
      if (this.addCandidateModalData?.scheduleResendModal) {
        this.scheduleResendCandidates(values);
      } else {
        this.submitCandidate(values);
      }
    },

    minuteDifference(date1, date2) {
      const startDate = new Date(date1);
      const endDate = new Date(date2);
      const diffMs = Math.abs(startDate - endDate);
      return diffMs / (1000 * 60);
    },
    async scheduleResendCandidates(values) {
      try {
        this.buttonLoading = true;
        const isSchedule = new Date(values.dateRange.startDate) > new Date();
        // 30 dakikadan kısa ise bitiş tarihini 30 dakika sonrasına ayarla
        const endDate = this.minuteDifference(values.dateRange.startDate, values.dateRange.endDate) <= 30
          ? new Date(Date.now() + 30 * 60 * 1000)
          : values.dateRange.endDate;

        const {
          sent, sendedCandidates, unSendedCandidates, sendedCount,
        } = await hrvenueService.scheduleAndResendCandidate(
          this.interviewId,
          this.addCandidateModalData.bulkSchedule ? [] : [this.addCandidateModalData?.candidate?.id],
          !!this.addCandidateModalData.bulkSchedule,
          this.addCandidateModalData?.candidatesStatus,
          isSchedule,
          values.dateRange.startDate,
          endDate,
          values.sendMail,
        );

        if (sent) {
          this.$emit('removeFilter', 'status');
          this.showSuccessStep = true;
          this.sendedCandidates = sendedCandidates;
          this.unSendedCandidates = unSendedCandidates;
          this.totalSendedCount = sendedCount;
        }

        this.buttonLoading = false;
      } catch (error) {
        showNotification(this.resource['Label.Error'], error.message, 'error');
        this.buttonLoading = false;
      }
    },
    async submitCandidate(values) {
      try {
        this.buttonLoading = true;
        const candidatesToSubmit = this.addCandidateModalType === 'one-by-one-modal'
          ? this.candidate.candidates
          : values.selectedCandidates;
        if (candidatesToSubmit.length > this.sendableCount) {
          return showNotification(
            this.resource['Label.Error'],
            this.resource['candidateDetail.candidateNumberExceedError'],
            'error',
          );
        }

        const isSchedule = new Date(values.dateRange.startDate) > new Date();
        // 30 dakikadan kısa ise bitiş tarihini 30 dakika sonrasına ayarla
        const endDate = this.minuteDifference(values.dateRange.startDate, values.dateRange.endDate) <= 30
          ? new Date(Date.now() + 30 * 60 * 1000)
          : values.dateRange.endDate;

        if (!this.candidate.id) {
          const { candidates } = await hrvenueService.checkInterviewSendableStatus(
            this.interviewId,
            candidatesToSubmit,
            isSchedule,
            values.dateRange.startDate,
            endDate,
          );
          if (candidates) {
            if (!this.sendableStatusShow(candidates).length) {
              const {
                sent, sendedCandidates, unSendedCandidates, sendedCount,
              } = await hrvenueService.sendInterviewOk(
                this.interviewId,
                candidatesToSubmit,
                isSchedule,
                values.dateRange.startDate,
                endDate,
                values.sendMail,
                this.addCandidateModalData?.scheduleResendModal || false,
              );
              if (sent) {
                this.getCredit();
                this.$emit('GetData'); // TODO: bu her zaman gelmeyelibir kontrol eklenmeli
                this.showSuccessStep = true;
                this.sendedCandidates = sendedCandidates;
                this.unSendedCandidates = unSendedCandidates;
                this.totalSendedCount = sendedCount;
                const sendMethod = {
                  'one-by-one-modal': 'add-one-by-one',
                  'excel-modal': 'add-with-excel',
                  'ats-modal': 'add-with-ats',
                };
                if (!this.addCandidateModalData?.scheduleResendModal) {
                  dataLayer.push({
                    event: 'ga4_event',
                    event_name: 'send_interview',
                    page_type: 'interview-detail',
                    event_parameters: {
                      send_method: sendMethod[this.addCandidateModalType],
                    },
                    site_type: 'interview',
                  });
                }
              }
            }
          }
        }
        this.buttonLoading = false;
      } catch (error) {
        showNotification(
          this.resource['Label.Error'],
          this.resource['interviewCreate.ProcessDuringError'],
          'error',
        );
      }
    },
    async getCredit() {
      const { totalUsableRight } = await hrvenueService.getCreditRight();
      this.creditCount = totalUsableRight >= 0 ? totalUsableRight : 0;
    },
    sendableStatusShow(candidatesList) {
      const beforeSendedCandidatesEmail = [];
      candidatesList.forEach((candidate) => { if (candidate.reasonForNotSending === 4) beforeSendedCandidatesEmail.push(candidate.email); });
      if (beforeSendedCandidatesEmail.length) {
        showNotification(
          this.resource['Label.Error'],
          `${this.resource['interviewCreate.AlreadySentEmail']} <br> ${beforeSendedCandidatesEmail.join('<br>')}`,
          'error',
        );
      }
      return beforeSendedCandidatesEmail;
    },
    onValidateField(candidate) {
      setTimeout(() => {
        let isValid = false;
        if (!this.validateFields(candidate)) {
          isValid = true;
          this.$refs.observer.validate();
        }
        return isValid;
      }, 150);
    },
    checkValidAllField() {
      let isValid = false;
      for (let i = 0; i < this.candidate.candidates.length; i += 1) {
        if (!this.validateFields(this.candidate.candidates[i])) {
          isValid = true;
          this.$refs.observer.validate();
          break;
        }
      }
      return isValid;
    },
    validateFields(obj) {
      if (!(obj.name || obj.email || obj.surname)) {
        return false;
      }
      const validEmail = /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)$/.test(obj.email);
      const validLength = (obj.name && obj.name.length >= 2 && obj.surname && obj.surname.length >= 2 && obj.email && obj.email.length >= 10 && obj.email.length <= 300);
      if (validEmail && validLength) {
        return true;
      }
      return false;
    },
    downloadExcel() {
      const filename = `${this.resource.candidateUrlExcelName}.xlsx`;
      const data = this.sendedCandidates;
      const ws = utils.json_to_sheet(data.map((d) => ({
        [this.resource['userHeader.Name']]: d.name,
        [this.resource['userHeader.Surname']]: d.surname,
        [this.resource['userHeader.Email']]: d.email,
        [this.resource['userHeader.Url']]: d.url,
      })));
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, this.resource.candidateExcelTabName);
      writeFile(wb, filename);
    },
  },
};
</script>

<style lang="scss">
@import "~@/style/starter.scss";
.one-by-one-modal,
.selection-screen {
  &:has(.schedule-area--schedule-resend) {
    .modal-body {
      max-height: 638px;

      @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
        max-height: 100vh;
      }
    }

    .one-by-one-modal__body {
      height: 515px;
      padding: 24px 32px;

      @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
        height: calc(100vh - 130px);
      }
    }
  }
  .modal-dialog {
    max-width: 603px !important;
  }
  .modal-body {
    max-height: 615px;
  }
  &__body {
    height: 490px;
    padding: 16px 32px;

    &:has(.one-by-one-modal__success) {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 326px;
    }

    &:has(.selection-screen__success) {
      height: 502px;
    }
  }
  &__footer {
    justify-content: flex-end;
  }
  .interview-input-group {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}
.excel-modal,
.ats-modal {
  .modal-dialog {
    max-width: 740px !important;
  }
  .modal-body {
    max-height: 615px;
  }
  &__body {
    height: 540px;
    padding: 24px 32px 32px;

    &:has(.candidates-list) {
      padding: 0 24px 16px;
    }

    &:has(.excel-modal__success),
    &:has(.ats-modal__success) {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 480px;
    }
  }
  &__footer {
    justify-content: space-between;

    &:has(.download-btn) {
      justify-content: flex-end;
    }
  }
  .interview-input-group {
    width: 100%;
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    align-items: center;

    span {
      flex: 1;
      margin-right: 4px;

      &:last-child {
        margin-right: 0;
      }
    }
  }

  .bg-button,
  .border-button-create {
    min-width: 132px;
  }
}
.one-by-one-modal,
.excel-modal,
.ats-modal,
.selection-screen {
  .modal-dialog {
    @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
      width: 100%;
      max-width: 100% !important;
      margin: 0 !important;
    }
  }
  .modal-content {
    border-radius: 12px;
    border: 0 !important;

    @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
      max-height: 100vh;
      border-radius: 0 !important;
    }
  }

  .modal-header {
    border-bottom: 0;
    padding: 16px 16px 16px 32px;
    border-bottom: 1px solid #e5e5e5;

    @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
      padding: 16px;
    }

    i {
      font-size: 24px;
      color: #333333;
    }

    h2 {
      color: #333333;
      font-family: Roboto;
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px; /* 137.5% */
    }
  }

  &__footer {
    border-top: 1px solid #E5E5E5;
    padding: 16px 32px;
    gap: 8px;
    display: flex;

    .button-group {
      gap: 8px;
      display: flex;
      align-items: center;
    }
  }

  .modal-body {
    border-bottom: 0;
    padding: 0;

    @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
      max-height: 100%;
      height: 100vh;
    }

    img {
      margin-bottom: 14px;
    }

    h2 {
      color: #353535;
      text-align: center;
      font-feature-settings: "liga" off, "clig" off;
      font-family: Roboto;
      font-size: 20px;
      font-style: normal;
      font-weight: 700;
      line-height: 26px;
      margin-bottom: 8px;
    }

    p {
      color: #858585;
      text-align: center;
      font-feature-settings: "liga" off, "clig" off;
      font-family: Roboto;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      margin-bottom: 24px;
    }
  }

  &__body {
    overflow: overlay;
    scrollbar-gutter: stable both-edges;

    @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
      padding: 16px;
      height: calc(100vh - 130px);
    }

    &::-webkit-scrollbar {
      width: 7px !important;
    }

    &::-webkit-scrollbar-track {
      background-color: #fff !important;
      border-radius: 2px !important;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 2px !important;
      background: linear-gradient(to right, #D8D8D8 3px, #FFF 4px) !important;
    }
  }

  &__success {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 8px;
    width: 100%;

    img {
      margin-bottom: 0 !important;
    }

    h3 {
      color: var(--text-colors-text-1, #353535);
      text-align: center;
      font-feature-settings: 'liga' off, 'clig' off;
      /* Current Styles/H2 */
      font-family: Roboto;
      font-size: 20px;
      font-style: normal;
      font-weight: 700;
      line-height: 26px;
      margin-bottom: 0;
    }
    span {
      color: var(--text-colors-text-2, #858585);
      text-align: center;
      font-feature-settings: 'liga' off, 'clig' off;
      /* Current Styles/P Regular */
      font-family: Roboto;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
    }

    .candidate-link-area {
      display: flex;
      flex-direction: column;
      align-self: center;
      position: relative;
      margin-top: 16px;
      font-family: 'Roboto' !important;
      width: 100%;

      p {
        color: var(--Text-Colors-Primary-Gray-Text, #333) !important;
        font-feature-settings: 'liga' off, 'clig' off;
        /* Typography/Subtitle/3 - Medium */
        font-family: Roboto;
        font-size: 14px !important;
        font-style: normal !important;
        font-weight: 500 !important;
        line-height: 20px !important;
        margin-bottom: 4px !important;
        text-align: left !important;
      }

      &__button {
        position: absolute;
        right: 8px;
        top: 32px;
        border: 0;
        color: #FF8D06;
        font-family: 'Roboto' !important;
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.125rem;
      }

      &__input {
        width: 100%;
        height: 36px;
        border-radius: 4px;
        border: 1px solid #C3C3C3;
        background: #FFF;
        padding: 0 8px;
        padding-right: 32px !important;
        font-family: 'Roboto' !important;
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.125rem;
        color: #3A3A3A;

        &:focus {
          outline: none;
          border-color: #FF8D06 !important;
          box-shadow: 0px 0px 0px 4px rgba(255, 141, 6, 0.3) !important;
        }
      }
    }
  }

  .border-button-create {
    border: 1px solid #ff8d06;
    background: transparent !important;
    display: flex;
    padding: 9px 8px;
    height: 36px;
    justify-content: center;
    align-items: center;
    border-radius: 40px;
    transition: all 0.2s ease-in-out;
    gap: 0 !important;
    height: auto !important;

    span {
      color: #ff8d06;
      text-align: center;
      font-family: Roboto;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 18px;
      padding: 0 8px;
    }

    i {
      color: #ff8d06;
      font-size: 16px;
      margin-right: 0 !important;
    }

    &.disabled {
      border-color: transparent;
      cursor: auto !important;
    }

    &:hover {
      background: #ff8d06 !important;

      span,
      i {
        color: #fff;
      }
    }

    &:after {
      display: none;
    }
  }

  .bg-button {
    display: flex;
    padding: 9px 8px;
    height: 36px;
    justify-content: center;
    align-items: center;
    border-radius: 40px;
    border: 1px solid #ff8d06;
    background: #ff8d06;
    transition: all 0.2s ease-in-out;
    gap: 0 !important;
    height: auto !important;

    span {
      color: #fff;
      text-align: center;
      font-family: Roboto;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 18px;
      padding: 0 8px;
    }

    i {
      color: #fff;
      font-size: 16px;
      margin-right: 0 !important;
    }

    &.disabled {
      border-color: transparent;
      cursor: auto !important; // bu bozuk tooltip çıkmıyor.
      background:#EEE !important;

      span, i {
        color: #C3C3C3 !important;
      }
    }

    &:hover {
      background: #FF7415 !important;
    }

    &:after {
      display: none;
    }
  }

  .modal-progress {
    height: 4px;
    background-color: #D9D9D9;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: var(--progress-width, 0%);
      background-color: #ff8d06;
      transition: width 0.3s ease;
    }
  }
}
</style>
